"use client";

import { useTranslation } from "@/app/i18n/client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "../../ui/input";
import { cn } from "@/lib/utils";
import { Button } from "../../ui/button";
import { OTPValidatorSchema, generateOTPSchema } from "@/lib/validators/otp";
import { hideMobileNumberDigit } from "@/util/util";
import React, { useRef, useState } from "react";
import { CountdownTimer } from "@/components/countdown-timer";
import { sendOTPSignup, verifyOTPSignup } from "@/actions";
import toast from "react-hot-toast";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { Loader2 } from "lucide-react";
import { useContext } from "react";
interface otpResponseData {
    ref: string;
    transId: string;
    refCode: string;
}
interface OTPFormProps {
    numDigits?: number;
    type: "email" | "mobile";
    value: string;
    data: otpResponseData;
    onOTPSubmit?: (success: boolean) => void;
}

const OTPFormSignup: React.FC<OTPFormProps> = ({ numDigits = 6, type, value, data, onOTPSubmit }) => {
    const { t } = useTranslation();

    const [otp, setOtp] = useState<string[]>(new Array(numDigits).fill(""));
    const inpRefs = useRef(null);
    const [activeBtnSubmit, setActiveBtnSubmit] = useState(false);
    const [activeBtnResendOTP, setActiveBtnResendOTP] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const form = useForm<OTPValidatorSchema>({
        resolver: zodResolver(generateOTPSchema(numDigits)),
    });

    const [otpResponseData, setOtpResponseData] = useState({
        ref: data.ref,
        transId: data.transId,
        refCode: data.refCode,
    });

    const [timerValue, setTimerValue] = useState(300); // Mock Initial timer value

    const handleTimerUpdate = (currentTime: number) => {
        if (currentTime === 0) setActiveBtnResendOTP(true);
        setTimerValue(currentTime);
    };

    const onSubmit = (values: OTPValidatorSchema) => {
        setIsLoading(true);
        const otpValue = Object.values(values).join("");
        verifyOTPSignup(otpResponseData.ref, otpValue, otpResponseData.transId, value, otpResponseData.refCode)
            .then((res: any) => {
                if (res && res.result.success == true) {
                    setIsLoading(false);
                    if (onOTPSubmit) onOTPSubmit(true);
                }
            })
            .catch((error: any) => {
                toast.error(`${t("signup.otp.toast_failed_message")}`, {
                    position: "bottom-center",
                    duration: 3000,
                });
            })
            .finally(() => {
                setTimeout(() => {
                    setIsLoading(false);
                }, 3000);
            });
    };

    const onSubmitResendOTP = () => {
        sendOTPSignup(value).then((res) => {
            setOtpResponseData({
                ref: res.result.ref,
                transId: res.result.transId,
                refCode: res.result.refCode,
            });
        });
        setTimerValue(60);
        setActiveBtnResendOTP(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (index > 0 && e.key === "Backspace" && !otp[index] && inpRefs?.current?.[index - 1]) {
            //@ts-ignore
            inpRefs.current[index - 1]?.focus();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;

        if (value.length > 1) {
            const newOtp = [...otp];
            const pastedValues = value.split("").slice(0, numDigits);
            for (let i = 0; i < pastedValues.length; i++) {
                if (index + i < numDigits) {
                    newOtp[index + i] = pastedValues[i];
                    form.setValue(`otp_${index + i}`, pastedValues[i]);
                }
            }
            setOtp(newOtp);

            const nextInput = inpRefs?.current?.[index + pastedValues.length];
            //@ts-ignore
            nextInput?.focus();
            setActiveBtnSubmit(newOtp.join("").length === numDigits);
            return;
        }

        if (!isNaN(parseInt(value))) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            form.setValue(`otp_${index}`, value.charAt(0));
            const nextInput = inpRefs?.current?.[index + 1];
            //@ts-ignore
            nextInput?.focus();
            if (index == numDigits - 1) setActiveBtnSubmit(true);
        }
    };
    const colorStyle = useContext(ColorStyleContext);

    return (
        <>
        <div className="flex items-center justify-center">
            <span className="font-normal text-xl" style={{ color: colorStyle?.text || "#3d3d3d"}}>{t("signup.otp.title")}</span>
        </div>
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} ref={inpRefs} className="space-y-4 p-6">
                <FormDescription
                    className="font-light text-sm"
                    style={{
                        color: colorStyle?.text || "#3d3d3d",
                    }}>
                    {type == "mobile"
                        ? t("signup.otp.text_sent_mobile_otp", {
                              mobile: `${hideMobileNumberDigit(value)}`,
                          })
                        : t("signup.otp.text_sent_email_otp", {
                              email: `${hideMobileNumberDigit(value)}`,
                          })}
                </FormDescription>
                <div className="flex flex-row items-center justify-between mx-auto w-full gap-5">
                    {Array.from({ length: numDigits }).map((_, index) => (
                        <div key={index} className="w-auto max-w-14 h-16">
                            <FormField
                                control={form.control}
                                name={`otp_${index}`}
                                render={({ field, formState }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                {...field}
                                                value={field.value || ""}
                                                onKeyDown={(e) => handleKeyDown(e, index)}
                                                onChange={(e) => {
                                                    if (e.target.value === "") {
                                                        const newOtp = [...otp];
                                                        newOtp[index] = "";
                                                        setOtp(newOtp);
                                                        form.setValue(`otp_${index}`, "");
                                                        setActiveBtnSubmit(newOtp.every((digit) => digit !== ""));
                                                    } else {
                                                        form.setValue(`otp_${index}`, e.target.value.charAt(0));
                                                        handleChange(e, index);
                                                    }
                                                }}
                                                className={cn(
                                                    "form-input form-input-hide-arrow border-gray-200 text-center",
                                                    formState.errors[`otp_${index}`] && "form-input-error",
                                                )}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    ))}
                </div>
                {/* show ref code */}
                <div>
                    <FormDescription
                        className="font-light text-sm opacity-50"
                        style={{
                            color: colorStyle?.text || "#3d3d3d",
                        }}>
                        {t("signup.ref_code")} {otpResponseData.refCode}
                    </FormDescription>
                </div>
                <div className="w-full flex flex-col md:flex-row items-center justify-between gap-5">
                    <button
                        disabled={!activeBtnSubmit || isLoading}
                        type="submit"
                        className={`mb-2 md:mb-0 h-[48px] w-full dynamic-bg rounded-full  dynamic-color dynamic-border flex items-center justify-center text-lg font-normal ${activeBtnSubmit ? "cursor-pointer" : ""}`}
                        style={
                            {
                                "--dynamic-bg": activeBtnSubmit ? colorStyle?.buttonBgNormal : "#f8fafc",
                                "--dynamic-hover-bg": activeBtnSubmit ? colorStyle?.buttonBgHover : "#f8fafc",
                                "--dynamic-color": activeBtnSubmit ? colorStyle?.buttonTextNormal : "#9397a0",
                                "--dynamic-hover-color": activeBtnSubmit ? colorStyle?.buttonTextHover : "#9397a0",
                                "--dynamic-focus-color": activeBtnSubmit ? colorStyle?.buttonTextHover : "#9397a0",
                            } as React.CSSProperties
                        }>
                        <div className="flex items-center justify-center gap-2">
                            {isLoading && <Loader2 className="animate-spin w-4 h-4" />}
                            {t("signup.otp.btn_confirm_otp")}
                        </div>
                    </button>
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={onSubmitResendOTP}
                        disabled={!activeBtnResendOTP}
                        className="w-full ">
                        {activeBtnResendOTP ? (
                            <>{t("signup.otp.btn_resend_otp")}</>
                        ) : (
                            <>
                                {t("signup.otp.btn_not_receive_otp")}{" "}
                                {timerValue > 0 && (
                                    <CountdownTimer duration={timerValue} onTimerUpdate={handleTimerUpdate} />
                                )}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </Form>
        </>
    );
};

export default OTPFormSignup;
